import { QueryFunctionContext } from '@tanstack/react-query';
import { api } from '../services/api';
import { IData, IDataNoPagination } from '../types/shared';

export interface INfse {
  id: number;
  numero_nfse?: string;
  numero_dps?: string;
  serie?: string;
  tipo?: string;
  arquivo?: string;
  codi_emp?: string;
  empresa_id?: number;
  data_emissao?: string;
  data_competencia?: string;
  dh_processamento?: string;
  ambiente?: string;
  status?: string;

  // Emissor
  emissor_cnpj?: string;
  emissor_cpf?: string;
  emissor_im?: string;
  emissor_nome?: string;
  emissor_nome_fantasia?: string;
  emissor_email?: string;
  emissor_fone?: string;
  emissor_municipio?: string;
  emissor_nome_municipio?: string;
  emissor_uf?: string;
  emissor_cep?: string;
  emissor_logradouro?: string;
  emissor_numero?: string;
  emissor_complemento?: string;
  emissor_bairro?: string;

  // Prestador
  prestador_nome?: string;
  prestador_cnpj?: string;
  prestador_cpf?: string;
  prestador_im?: string;
  prestador_email?: string;
  prestador_fone?: string;
  prestador_municipio?: string;
  prestador_nome_municipio?: string;
  prestador_uf?: string;
  prestador_cep?: string;
  prestador_logradouro?: string;
  prestador_numero?: string;
  prestador_complemento?: string;
  prestador_bairro?: string;

  // Tomador
  tomador_nome?: string;
  tomador_cnpj?: string;
  tomador_cpf?: string;
  tomador_im?: string;
  tomador_email?: string;
  tomador_fone?: string;
  tomador_municipio?: string;
  tomador_nome_municipio?: string;
  tomador_uf?: string;
  tomador_cep?: string;
  tomador_logradouro?: string;
  tomador_numero?: string;
  tomador_complemento?: string;
  tomador_bairro?: string;

  // Valores
  valor_serv_prest?: number;
  valor_bc?: number;
  valor_issqn?: number;
  valor_liq?: number;
  valor_total_ret?: number;
  valor_calc_dr?: number;
  aliquota_aplic?: number;

  // Localização
  local_emissao?: string;
  local_prestacao?: string;
  local_incidencia?: string;
  tributo_nacional?: string;

  // Serviço
  servico_codigo?: string;
  servico_codigo_nbs?: string;
  servico_codigo_trib_mun?: string;
  servico_descricao?: string;
  servico_local_prestacao?: string;
  servico_info_complementar?: string;

  // Outras informações
  outras_informacoes?: string;

  // Dados completos (JSON/XML)
  dados_completos?: any;

  // Campos de cancelamento
  cancelada?: boolean;
  data_cancelamento?: string | null;
  motivo_cancelamento?: string | null;
  chave_substituta?: string | null;
  chave_nfse?: string | null;

  // Campos de impostos
  iss?: number;
  issqn?: number;
  inss?: number;
  cofins?: number;
  pis?: number;
  csll?: number;
  ir?: number;
  irrf?: number;
  irpj?: number;

  // Campos de retenção de impostos
  iss_retido?: boolean;
  issqn_retido?: boolean;
  inss_retido?: boolean;
  irrf_retido?: boolean;
  ir_retido?: boolean;
  irpj_retido?: boolean;
  csll_retido?: boolean;
  cofins_retido?: boolean;
  pis_retido?: boolean;

  // Campos de município de retenção
  municipio_retencao_imposto?: string | null;
  uf_retencao_imposto?: string | null;

  // Novos campos de retenção (Portal Nacional NFS-e)
  // ISSQN (Municipal)
  trib_issqn?: string;
  tp_ret_issqn?: string;

  // PIS/COFINS (Federal)
  cst?: string;
  vbc_pis_cofins?: number;
  p_aliq_pis?: number;
  p_aliq_cofins?: number;
  v_pis?: number;
  v_cofins?: number;
  tp_ret_pis_cofins?: string;

  // Valores de retenção
  v_ret_irrf?: number;
  v_ret_csll?: number;
  v_ret_inss?: number;

  // Percentuais totais de tributos
  p_tot_trib_fed?: number;
  p_tot_trib_est?: number;
  p_tot_trib_mun?: number;
}

export interface INfseStats {
  total: number;
  total_prestados: number;
  total_tomados: number;
  valor_total: number;
  valor_total_prestados: number;
  valor_total_tomados: number;
  // Contagem de notas por tipo de retenção
  total_com_retencao_federal: number;
  total_com_retencao_municipal: number;
  total_com_retencao_pis_cofins_tp2: number; // tpRetPisCofins em 1/3/4/5/6/7/9 (retenção PIS e/ou COFINS, NT-007)
  total_sem_retencao: number;
  // Status das notas — exclusivos: ativas + canceladas + substituídas = total
  total_canceladas: number;
  total_substituidas: number;
  total_ativas: number;
}

export interface INfseDashboardEmpresa {
  empresa_id: number;
  codi_emp?: string;
  razao_social?: string;
  cnpj?: string;
  total_notas: number;
  total_prestadas: number;
  total_tomadas: number;
  // Exclusivos: ativas + canceladas + substituídas = total_notas
  total_canceladas: number;
  total_substituidas?: number;
  valor_total: number;
  valor_prestadas: number;
  valor_tomadas: number;
  tem_retencao_issqn: boolean;
  tem_retencao_pis_cofins: boolean;
  tem_retencao_pis_cofins_tp2: boolean; // tpRetPisCofins em 1/3/4/5/6/7/9 (retenção PIS e/ou COFINS, NT-007)
  notas_ret_pis_cofins?: number; // nº de notas ativas com código de retenção PIS/COFINS
  tem_retencao_irrf: boolean;
  tem_retencao_csll: boolean;
  tem_retencao_inss: boolean;
  // ISSQN RETIDO das notas ativas (não é o ISSQN total da empresa)
  valor_issqn: number;
  valor_pis: number;
  valor_cofins: number;
  valor_irrf: number;
  valor_csll: number;
  valor_inss: number;
}

export interface INfseDashboard {
  total_notas: number;
  total_notas_prestadas: number;
  total_notas_tomadas: number;
  // Exclusivos: ativas + canceladas (sem substituição) + substituídas = total_notas
  total_notas_canceladas: number;
  total_notas_substituidas?: number;
  total_notas_ativas: number;
  valor_total: number;
  valor_total_prestadas: number;
  valor_total_tomadas: number;
  valor_total_ativas: number;
  valor_total_canceladas: number;
  valor_total_substituidas?: number;
  total_empresas: number;
  empresas_com_notas_prestadas: number;
  empresas_com_notas_tomadas: number;
  empresas_com_notas_canceladas: number;
  // Backend conta aqui só empresa sem cancelamento comum (a que tem os dois entra em canceladas)
  empresas_com_notas_substituidas?: number;
  empresas_com_retencao_issqn: number;
  empresas_com_retencao_pis_cofins: number;
  empresas_com_retencao_pis_cofins_tp2: number; // tpRetPisCofins em 1/3/4/5/6/7/9 (retenção PIS e/ou COFINS, NT-007)
  empresas_com_retencao_irrf: number;
  empresas_com_retencao_csll: number;
  empresas_com_retencao_inss: number;
  valor_total_issqn: number;
  valor_total_pis: number;
  valor_total_cofins: number;
  valor_total_irrf: number;
  valor_total_csll: number;
  valor_total_inss: number;
  empresas_detalhes?: INfseDashboardEmpresa[];
}

// Novos tipos para relatórios prestados/tomados
export interface IRelatorioStatics {
  total_registros: number;
  total_prestados: number;
  total_tomados: number;
  total_empresas: number;
  empresas_com_relatorio: number;
  empresas_com_relatorio_pct: number;
  empresas_sem_relatorio: number;
  empresas_sem_relatorio_pct: number;
  empresas_homologadas: number;
  empresas_homologadas_pct: number;
  empresas_nao_homologadas: number;
  empresas_nao_homologadas_pct: number;
  outros_erros: number;
  outros_erros_pct: number;
  com_anotacao: number;
  com_anotacao_pct: number;
  ok_valor_prestados: number;
  ok_valor_prestados_pct: number;
  nok_valor_prestados: number;
  nok_valor_prestados_pct: number;
  ok_quantidade_prestados: number;
  ok_quantidade_prestados_pct: number;
  nok_quantidade_prestados: number;
  nok_quantidade_prestados_pct: number;
  aguardando_homologacao_prestados: number;
  ok_valor_tomados: number;
  ok_valor_tomados_pct: number;
  nok_valor_tomados: number;
  nok_valor_tomados_pct: number;
  ok_quantidade_tomados: number;
  ok_quantidade_tomados_pct: number;
  nok_quantidade_tomados: number;
  nok_quantidade_tomados_pct: number;
  aguardando_homologacao_tomados: number;
}

export interface IRelatorioGeral {
  id_emp: number;
  codi_emp: string;
  razao_social: string;
  cnpj: string;
  cidade_estado?: string;
  // Notas Prestadas (SIEG saídas)
  total_notas_prestadas: number | null;
  total_valor_prestados: number | null;
  // Notas Tomadas (SIEG entradas)
  total_notas_tomadas: number | null;
  total_valor_tomados: number | null;
  // Domínio - Saídas
  vl_servico: number | null;
  qt_nfse_dominio_saidas: number | null;
  // Domínio - Entradas
  qt_nfse_dominio_entradas: number | null;
  vl_nfse_dominio_entradas: number | null;
  // PDFs
  pdf_path_prestados?: string | null;
  pdf_path_tomados?: string | null;
  // Anotações
  qtd_anotacoes?: number;
}

export interface IRelatorioPrestadosTomados {
  id: number;
  codi_emp: string;
  razao_social: string;
  cnpj: string;
  numero_nfse: string;
  data_emissao: string;
  valor_servico: number;
  prestador_nome: string;
  prestador_cnpj: string;
  tomador_nome: string;
  tomador_cnpj: string;
  fonte: 'sieg' | 'faturamento' | 'dominio';
}

export const useNfse = () => {
  const cleanFilters = (filters: any) => {
    const cleaned: any = {};
    Object.keys(filters).forEach(key => {
      const value = filters[key];
      if (value !== '' && value !== null && value !== undefined) {
        cleaned[key] = value;
      }
    });
    return cleaned;
  };

  const getNfseList = async (key: QueryFunctionContext): Promise<IData<INfse[]>> => {
    const filters = key.queryKey?.[1] ?? {};
    const cleanedFilters = cleanFilters(filters);
    const res = await api.get<IData<INfse[]>>('/api/v1/nfse/', {
      params: cleanedFilters,
    });
    return res.data;
  };

  const getAllNfse = async (filters: Record<string, any>): Promise<INfse[]> => {
    const cleanedFilters = cleanFilters(filters);

    // Primeira requisição para descobrir o total de páginas
    const firstRes = await api.get<IData<INfse[]>>('/api/v1/nfse/', {
      params: { ...cleanedFilters, page: 1, size: 100 },
    });

    const { items: firstItems, pages: totalPages } = firstRes.data.data;

    if (totalPages <= 1) {
      return firstItems;
    }

    // Busca as demais páginas em paralelo
    const remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);
    const promises = remainingPages.map(page =>
      api.get<IData<INfse[]>>('/api/v1/nfse/', {
        params: { ...cleanedFilters, page, size: 100 },
      }),
    );

    const results = await Promise.all(promises);
    const allItems = [...firstItems, ...results.flatMap(res => res.data.data.items)];

    return allItems;
  };

  const getNfseStats = async (
    key: QueryFunctionContext,
  ): Promise<IDataNoPagination<INfseStats>> => {
    const filters = key.queryKey?.[1] ?? {};
    const cleanedFilters = cleanFilters(filters);
    const res = await api.get<IDataNoPagination<INfseStats>>('/api/v1/nfse/stats/', {
      params: cleanedFilters,
    });
    return res.data;
  };

  const getNfseById = async (id: number): Promise<IData<INfse>> => {
    const res = await api.get<IData<INfse>>(`/api/v1/nfse/${id}/`);
    return res.data;
  };

  const getMunicipiosRetencaoNfse = async (
    key: QueryFunctionContext,
  ): Promise<IDataNoPagination<string[]>> => {
    const filters = key.queryKey?.[1] ?? {};
    const cleanedFilters = cleanFilters(filters);
    const res = await api.get<IDataNoPagination<string[]>>('/api/v1/nfse/municipios-retencao/', {
      params: cleanedFilters,
    });
    return res.data;
  };

  const getNfseDashboard = async (
    key: QueryFunctionContext,
  ): Promise<IDataNoPagination<INfseDashboard>> => {
    const filters = key.queryKey?.[1] ?? {};
    const cleanedFilters = cleanFilters(filters);
    const res = await api.get<IDataNoPagination<INfseDashboard>>('/api/v1/nfse/dashboard/', {
      params: cleanedFilters,
    });
    return res.data;
  };

  const getNfseDashboardFilters = async (
    key: QueryFunctionContext,
  ): Promise<IDataNoPagination<Record<string, string[]>>> => {
    const filters = key.queryKey?.[1] ?? {};
    const cleanedFilters = cleanFilters(filters);
    const res = await api.get<IDataNoPagination<Record<string, string[]>>>(
      '/api/v1/nfse/dashboard/filters/',
      {
        params: cleanedFilters,
      },
    );
    return res.data;
  };

  // Novos endpoints para relatórios prestados/tomados
  const getRelatorioStatics = async (key: QueryFunctionContext): Promise<IRelatorioStatics> => {
    const filters = key.queryKey?.[1] ?? {};
    const cleanedFilters = cleanFilters(filters);
    const res = await api.get<IRelatorioStatics>('/api/v1/relatorios-prestados-tomados/statics', {
      params: cleanedFilters,
    });
    return res.data;
  };

  const getRelatorioGeral = async (
    key: QueryFunctionContext,
  ): Promise<{
    items: IRelatorioGeral[];
    total: number;
    page: number;
    size: number;
    pages: number;
  }> => {
    const filters = key.queryKey?.[1] ?? {};
    const cleanedFilters = cleanFilters(filters);
    const res = await api.get<{
      items: IRelatorioGeral[];
      total: number;
      page: number;
      size: number;
      pages: number;
    }>('/api/v1/relatorios-prestados-tomados/geral', {
      params: cleanedFilters,
    });
    return res.data;
  };

  const getRelatorioPorTipo = async (
    key: QueryFunctionContext,
  ): Promise<IData<IRelatorioPrestadosTomados[]>> => {
    const [, tipo, filters] = key.queryKey as [string, string, any];
    const cleanedFilters = cleanFilters(filters);
    const res = await api.get<IData<IRelatorioPrestadosTomados[]>>(
      `/api/v1/relatorios-prestados-tomados/${tipo}`,
      {
        params: cleanedFilters,
      },
    );
    return res.data;
  };

  const getPdfUrl = async (key: string): Promise<string> => {
    const res = await api.get<{ url: string }>('/api/v1/relatorios-prestados-tomados/pdf-url', {
      params: { key },
    });
    return res.data.url;
  };

  return {
    getNfseList,
    getAllNfse,
    getNfseStats,
    getNfseById,
    getMunicipiosRetencaoNfse,
    getNfseDashboard,
    getNfseDashboardFilters,
    getRelatorioStatics,
    getRelatorioGeral,
    getRelatorioPorTipo,
    getPdfUrl,
  };
};
